import React, {
  Suspense,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import BodyModel from './BodyModel';
import {
  IonPage,
  IonContent,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonText,
  IonSpinner,
  IonButtons,
  IonBackButton,
  IonIcon,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import { informationCircle, moveOutline, refreshOutline } from 'ionicons/icons';
import './ModelPage.css';

import { firestore } from '../../../firebase';
import { collection, getDocs } from 'firebase/firestore';

const ModelPage: React.FC = () => {
  const [modalData, setModalData] = useState<{
    muscle: string;
    isOpen: boolean;
  }>({
    muscle: '',
    isOpen: false,
  });
  const [suggestedMachine, setSuggestedMachine] = useState<string | null>(null);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [canvasKey, setCanvasKey] = useState(0);
  const [renderCanvas, setRenderCanvas] = useState(true);
  const orbitRef = useRef<any>(null);

  const resetCamera = () => {
    if (orbitRef.current) {
      orbitRef.current.reset();
    }
  };

  const forceRecreateCanvas = () => {
    setRenderCanvas(false);
    setTimeout(() => {
      setCanvasKey(prev => prev + 1);
      setRenderCanvas(true);
    }, 50);
  };

  useIonViewDidEnter(() => {
    forceRecreateCanvas();
  });

  useIonViewWillLeave(() => {
    setRenderCanvas(false);
  });

  useEffect(() => {
    if (!modalData.isOpen || !modalData.muscle) {
      setSuggestedMachine(null);
      return;
    }

    const fetchAndSuggest = async () => {
      try {
        const snap = await getDocs(collection(firestore, 'machines'));
        const all = snap.docs.map(doc => ({
          id: doc.id,
          ...(doc.data() as any),
        }));
        const matches = all.filter(
          m =>
            Array.isArray(m.exercises) &&
            m.exercises.some(
              (ex: any) =>
                Array.isArray(ex.muscles) &&
                ex.muscles.includes(modalData.muscle)
            )
        );
        if (matches.length) {
          const pick = matches[Math.floor(Math.random() * matches.length)];
          setSuggestedMachine(pick.title);
        } else {
          setSuggestedMachine(null);
        }
      } catch (err) {
        console.error('Error fetching machines:', err);
        setSuggestedMachine(null);
      }
    };

    fetchAndSuggest();
  }, [modalData]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/my/advisor" />
          </IonButtons>
          <IonTitle>3D Advisor</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setInfoModalOpen(true)} shape="round">
              <IonIcon icon={informationCircle} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="model-page-content" scrollY={false}>
        <div
          className="model-stage"
        >
          <div className="model-guide">
            <span>Interactive muscle map</span>
            <strong>Tap a muscle to explore</strong>
            <small><IonIcon icon={moveOutline} /> Drag to rotate · pinch to zoom</small>
          </div>
          {renderCanvas && (
            <Canvas
              key={canvasKey}
              className="model-canvas"
              camera={{ position: [0, 0.6, 5.4], fov: 46, near: 0.1, far: 1000 }}
            >
              <color attach="background" args={['#e9e2ee']} />
              <Suspense
                fallback={
                  <mesh>
                    <boxGeometry />
                    <meshBasicMaterial color="grey" />
                    <Html center>
                      <IonSpinner name="crescent" />
                    </Html>
                  </mesh>
                }
              >
                <OrbitControls
                  ref={orbitRef}
                  makeDefault
                  enablePan={false}
                  enableZoom
                  minDistance={2}
                  maxDistance={10}
                />
                <BodyModel setModalData={setModalData} />
              </Suspense>
            </Canvas>
          )}
          <div className="model-stage-actions">
            <IonButton shape="round" fill="solid" onClick={resetCamera}><IonIcon icon={refreshOutline} slot="start" />Reset view</IonButton>
            <IonButton shape="round" fill="solid" onClick={() => setInfoModalOpen(true)} aria-label="How to use the 3D model"><IonIcon icon={informationCircle} slot="icon-only" /></IonButton>
          </div>
        </div>

        {/* Muscle modal */}
        <IonModal
          isOpen={modalData.isOpen}
          onDidDismiss={() => setModalData({ muscle: '', isOpen: false })}
          initialBreakpoint={0.42}
          breakpoints={[0, 0.42, 0.68]}
          handleBehavior="cycle"
          className="muscle-result-sheet"
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>{modalData.muscle}</IonTitle>
            </IonToolbar>
          </IonHeader>

          <IonContent className="ion-padding muscle-result-content">
            <IonText>
              <p>
                A matching option from your catalogue is{' '}
                <strong>{suggestedMachine ?? 'still being selected'}</strong>.
              </p>
              <p>Open the catalogue to compare every machine that targets {modalData.muscle}.</p>
            </IonText>

            <IonButton
              expand="block"
              color="primary"
              routerLink={`/my/machines?muscle=${encodeURIComponent(modalData.muscle)}`}
              routerDirection="forward"
            >
              Show matching machines
            </IonButton>
          </IonContent>
        </IonModal>
        {/* Info modal */}
        <IonModal
          isOpen={infoModalOpen}
          onDidDismiss={() => setInfoModalOpen(false)}
          initialBreakpoint={0.35}
          breakpoints={[0, 0.35, 0.5, 0.8]}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>3D Advisor Info</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setInfoModalOpen(false)} />
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          <IonContent className="ion-padding">
            <p>
              This 3D model lets you explore different muscle groups. Tap on a
              muscle to see what machines are best for training it. You can
              rotate and zoom using touch gestures.
            </p>

            <IonButton expand="block" onClick={resetCamera}>
              Reset View
            </IonButton>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default ModelPage;
